import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * StandardQuickFilters Component
 * 
 * A reusable component for displaying quick filter options.
 * Supports customizable filter buttons with icons, counts, and active states.
 * 
 * @param {Object} props
 * @param {Array} props.filters - Array of filter configurations
 * @param {string} props.title - Title for the quick filters section
 * @param {Object} props.activeFilters - Currently active filters
 * @param {Function} props.onApplyFilter - Callback when a filter is clicked
 * @param {Object} props.customStyles - Optional custom styles for the component
 */
const StandardQuickFilters = ({
  filters = [],
  title = "Quick Filters",
  activeFilters = {},
  onApplyFilter = () => {},
  customStyles = {},
  compact = false,
}) => {
  // Helper function to check if a filter is active
  const isFilterActive = (filter) => {
    return activeFilters[filter.field] === filter.value;
  };

  // Helper function to get filter button styles
  const getFilterButtonStyles = (filter) => {
    const isActive = isFilterActive(filter);
    const colors = filter.colors || {
      active: {
        bg: '#EFF6FF',
        text: '#1E40AF',
        border: '#3B82F6'
      },
      default: {
        bg: '#F9FAFB',
        text: '#374151',
        border: '#E5E7EB'
      },
      hover: {
        bg: '#EFF6FF',
        text: '#1E40AF',
        border: '#3B82F6'
      }
    };

    const locked = Boolean(filter.locked);

    return {
      display: 'flex',
      alignItems: 'center',
      gap: compact ? '5px' : '8px',
      padding: compact ? '4px 8px' : '6px 12px',
      borderRadius: '6px',
      cursor: locked ? 'default' : 'pointer',
      backgroundColor: isActive ? colors.active.bg : colors.default.bg,
      border: `1px solid ${isActive ? colors.active.border : colors.default.border}`,
      color: isActive ? colors.active.text : colors.default.text,
      transition: 'all 0.2s ease',
      ...(locked
        ? {
            '&:hover': {
              backgroundColor: isActive ? colors.active.bg : colors.default.bg,
              borderColor: isActive ? colors.active.border : colors.default.border,
              color: isActive ? colors.active.text : colors.default.text,
              transform: 'none',
              boxShadow: 'none',
            },
          }
        : {
            '&:hover': {
              backgroundColor: colors.hover.bg,
              borderColor: colors.hover.border,
              color: colors.hover.text,
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            },
          }),
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 3 },
        width: '100%',
        ...customStyles.container
      }}
    >
      {/* Title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 'fit-content',
          ...customStyles.titleContainer
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            color: '#111827',
            fontSize: compact ? '0.57rem' : '0.875rem',
            whiteSpace: 'nowrap',
            ...customStyles.title
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          flex: 1,
          ...customStyles.filtersContainer
        }}
      >
        {filters.map((filter, index) => (
          <Box
            key={index}
            title={filter.tooltip || undefined}
            onClick={() => {
              if (filter.locked) return;
              onApplyFilter(filter);
            }}
            sx={getFilterButtonStyles(filter)}
          >
            {filter.icon}
            <Typography
              sx={{
                fontSize: compact ? '0.57rem' : '0.875rem',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
            >
              {filter.label}
            </Typography>
            {filter.count != null && (
              <Box
                sx={{
                  minWidth: compact ? '13px' : '20px',
                  height: compact ? '13px' : '20px',
                  borderRadius: '10px',
                  backgroundColor: isFilterActive(filter) 
                    ? filter.colors?.active.text + '15' 
                    : '#F3F4F6',
                  color: isFilterActive(filter)
                    ? filter.colors?.active.text
                    : '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: compact ? '0.49rem' : '0.75rem',
                  fontWeight: 600,
                  padding: compact ? '0 4px' : '0 6px'
                }}
              >
                {filter.count}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StandardQuickFilters; 